/**
 * Canonical Operations Registry
 *
 * Central registry of all operations in the system.
 * Every endpoint operation must be registered here.
 */
import { AuthenticationAssuranceLevel, ScopeType, } from './operation.js';
import { ENTITLEMENT_EXEMPT_MODULES } from '../access/entitlements.js';
import { ACCOUNT_SECURITY_CAPABILITIES } from '../settings/capabilities.js';
import { PLATFORM_OPERATIONS } from './platform.operations.js';
import { PLATFORM_INTEGRATION_OPERATIONS } from './platform-integration.operations.js';
import { DASHBOARD_OPERATIONS } from './dashboard.operations.js';
import { INVESTMENT_OPERATIONS } from './investment.operations.js';
import { MARKET_DATA_OPERATIONS } from './market-data.operations.js';
import { PORTFOLIO_CUSTODY_OPERATIONS } from './portfolio-custody.operations.js';
import { SUSPENSE_CASH_OPERATIONS } from './suspense-cash.operations.js';
import { ORGANIZATION_GOVERNANCE_OPERATIONS } from './organization-governance.operations.js';
import { PORTFOLIO_PERFORMANCE_OPERATIONS, REPORTING_PERFORMANCE_RETURN_CALCULATE, REPORTING_PERFORMANCE_RETURN_LIST, REPORTING_PERFORMANCE_RETURN_CORRECTION_REQUEST, REPORTING_PERFORMANCE_RETURN_CORRECTION_APPROVE, REPORTING_PERFORMANCE_RETURN_CORRECTION_FINALIZE, REPORTING_FIXED_INCOME_VALUATION_CALCULATE, REPORTING_FIXED_INCOME_LIFECYCLE_SCHEDULE, REPORTING_FIXED_INCOME_LIFECYCLE_APPROVE, REPORTING_FIXED_INCOME_LIFECYCLE_POST, REPORTING_FIXED_INCOME_DEBT_EVENT_PROCESS, REPORTING_VALUATION_RUN_CREATE, REPORTING_VALUATION_RUN_LIST, REPORTING_VALUATION_RUN_APPROVE, REPORTING_VALUATION_RUN_PUBLISH, REPORTING_VALUATION_RUN_REOPEN_REQUEST, REPORTING_VALUATION_RUN_REOPEN_APPROVE, } from './portfolio-performance.operations.js';
import { TAX_RULE_OPERATIONS } from './tax-rule.operations.js';
import { FEE_RULE_OPERATIONS } from './fee-rule.operations.js';
import { SEARCH_OPERATIONS } from './search.operations.js';
import { ACCOUNTING_OPERATIONS, RECONCILIATION_OPERATIONS, REGULATORY_OPERATIONS, IMPORT_OPERATIONS, } from './accounting.operations.js';
import { ACCESS_OPERATIONS } from './access.operations.js';
import { ADMIN_OPERATIONS } from './admin.operations.js';
import { PLANNED_OPERATIONS } from './planned.operations.js';
import { MARKET_DATA_CORRECTION_OPERATIONS } from './market-data-correction.operations.js';
import { REPORT_EXPORT_OPERATIONS } from './report-export.operations.js';
import { normalizeMaintenanceDelegationOperation } from './maintenance-delegation-policy.js';
import * as OID from './operation-ids.js';
export const HIGH_SECURITY_AAL2_OPERATION_IDS = Object.freeze([
    // AUTH_PASSWORD_CHANGE is intentionally not high-security for the route
    // access gate; the actual change endpoint still enforces AAL2 via
    // @RequireAal in AuthController so the form renders at AAL1.
    OID.AUTH_SESSION_MANAGE,
    OID.AUTH_SESSION_REVOKE,
    OID.AUTH_ENTER_DELEGATION,
    OID.AUTH_EXIT_DELEGATION,
    OID.AUTH_DISABLE_MFA,
    OID.AUTH_REGENERATE_BACKUP_CODES,
    OID.ADMIN_AUDIT_VIEW,
    OID.PLATFORM_AUDIT_LIST,
    OID.PLATFORM_AUDIT_READ,
    OID.PLATFORM_AUDIT_EXPORT,
    OID.ORGANIZATION_AUDIT_LIST,
    OID.ORGANIZATION_AUDIT_READ,
    OID.ORGANIZATION_AUDIT_EXPORT,
    OID.ORGANIZATION_USERS_MFA_REQUIRE_ENROLLMENT,
    OID.ORGANIZATION_USERS_MFA_RESET_ENROLLMENT,
    OID.ORGANIZATION_USERS_MFA_DISABLE,
    OID.ORGANIZATION_USERS_MFA_REGENERATE_RECOVERY,
]);
const HIGH_SECURITY_AAL2_OPERATION_ID_SET = new Set(HIGH_SECURITY_AAL2_OPERATION_IDS);
export const DISABLED_ACCOUNT_SECURITY_OPERATION_IDS = Object.freeze(ACCOUNT_SECURITY_CAPABILITIES.mfaManagement
    ? []
    : [OID.SETTINGS_MFA, OID.AUTH_DISABLE_MFA, OID.AUTH_REGENERATE_BACKUP_CODES]);
const DISABLED_ACCOUNT_SECURITY_OPERATION_ID_SET = new Set(DISABLED_ACCOUNT_SECURITY_OPERATION_IDS);
const CANONICAL_PERMISSION_ID_BY_LEGACY_PERMISSION = Object.freeze({
    'accounting:manage:coa': 'organization:accounting:manage',
    'accounting:manage:fiscalPeriod': 'organization:accounting:manage',
    'accounting:manage:fiscalYear': 'organization:accounting:manage',
    'accounting:manage:journal': 'organization:accounting:manage',
    'accounting:manage:ledger': 'organization:accounting:manage',
    'accounting:manage:postingRule': 'organization:accounting:manage',
    'accounting:view:coa': 'organization:accounting:read',
    'accounting:view:fiscalPeriod': 'organization:accounting:read',
    'accounting:view:fiscalYear': 'organization:accounting:read',
    'accounting:view:journal': 'organization:accounting:read',
    'accounting:view:ledger': 'organization:accounting:read',
    'accounting:view:postingRule': 'organization:accounting:read',
    'reconciliation:create': 'organization:reconciliation:create',
    'reconciliation:manage': 'organization:reconciliation:manage',
    'reconciliation:view': 'organization:reconciliation:read',
    'compliance.nepal:generate': 'organization:reports:run',
    'compliance.nepal:export': 'organization:reports:export',
});
const PORTFOLIO_PERFORMANCE_PERMISSION_ID_BY_OPERATION = Object.freeze({
    [REPORTING_PERFORMANCE_RETURN_LIST]: 'organization:performance-returns:read',
    [REPORTING_PERFORMANCE_RETURN_CALCULATE]: 'organization:performance-returns:calculate',
    [REPORTING_PERFORMANCE_RETURN_CORRECTION_REQUEST]: 'organization:performance-returns:correction-request',
    [REPORTING_PERFORMANCE_RETURN_CORRECTION_APPROVE]: 'organization:performance-returns:correction-approve',
    [REPORTING_PERFORMANCE_RETURN_CORRECTION_FINALIZE]: 'organization:performance-returns:correction-finalize',
    [REPORTING_FIXED_INCOME_VALUATION_CALCULATE]: 'organization:fixed-income-valuations:calculate',
    [REPORTING_FIXED_INCOME_LIFECYCLE_SCHEDULE]: 'organization:fixed-income-lifecycle:schedule',
    [REPORTING_FIXED_INCOME_LIFECYCLE_APPROVE]: 'organization:fixed-income-lifecycle:approve',
    [REPORTING_FIXED_INCOME_LIFECYCLE_POST]: 'organization:fixed-income-lifecycle:post',
    [REPORTING_FIXED_INCOME_DEBT_EVENT_PROCESS]: 'organization:fixed-income-debt-events:process',
    [REPORTING_VALUATION_RUN_LIST]: 'organization:valuation-runs:read',
    [REPORTING_VALUATION_RUN_CREATE]: 'organization:valuation-runs:create',
    [REPORTING_VALUATION_RUN_APPROVE]: 'organization:valuation-runs:approve',
    [REPORTING_VALUATION_RUN_PUBLISH]: 'organization:valuation-runs:publish',
    [REPORTING_VALUATION_RUN_REOPEN_REQUEST]: 'organization:valuation-runs:reopen-request',
    [REPORTING_VALUATION_RUN_REOPEN_APPROVE]: 'organization:valuation-runs:reopen-approve',
});
const { ...EFFECTIVE_PLANNED_OPERATIONS } = PLANNED_OPERATIONS;
const MERGED_OPERATIONS_REGISTRY = {
    ...PLATFORM_OPERATIONS,
    ...PLATFORM_INTEGRATION_OPERATIONS,
    ...DASHBOARD_OPERATIONS,
    ...ACCESS_OPERATIONS,
    ...ADMIN_OPERATIONS,
    ...INVESTMENT_OPERATIONS,
    ...ACCOUNTING_OPERATIONS,
    ...RECONCILIATION_OPERATIONS,
    ...REGULATORY_OPERATIONS,
    ...IMPORT_OPERATIONS,
    ...EFFECTIVE_PLANNED_OPERATIONS,
    ...MARKET_DATA_OPERATIONS,
    ...MARKET_DATA_CORRECTION_OPERATIONS,
    ...PORTFOLIO_PERFORMANCE_OPERATIONS,
    ...PORTFOLIO_CUSTODY_OPERATIONS,
    ...SUSPENSE_CASH_OPERATIONS,
    ...ORGANIZATION_GOVERNANCE_OPERATIONS,
    ...TAX_RULE_OPERATIONS,
    ...FEE_RULE_OPERATIONS,
    ...SEARCH_OPERATIONS,
    ...REPORT_EXPORT_OPERATIONS,
};
const INSTRUMENT_AUTHORITY_MUTATION_IDS = new Set([
    OID.INSTRUMENT_CREATE,
    OID.INSTRUMENT_UPDATE,
    OID.INSTRUMENT_BULK_UPSERT,
]);
const MARKET_DATA_OPERATION_IDS = new Set([
    OID.MARKET_DATA_CREATE,
    OID.MARKET_DATA_LIST,
    OID.MARKET_DATA_GET,
    OID.MARKET_DATA_UPDATE,
    OID.MARKET_DATA_DELETE,
    OID.MARKET_DATA_BULK_UPSERT,
]);
const MARKET_DATA_READ_OPERATION_IDS = new Set([
    OID.MARKET_DATA_LIST,
    OID.MARKET_DATA_GET,
]);
function normalizeReferenceDataAuthority(operationId, descriptor) {
    if (INSTRUMENT_AUTHORITY_MUTATION_IDS.has(operationId)) {
        return {
            allowedPersonas: ['PLATFORM_ROOT', 'PLATFORM_ADMIN', 'ORG_ADMIN'],
            scopeTypes: [
                ScopeType.SYSTEM,
                ScopeType.PLATFORM,
                ScopeType.ORGANIZATION,
                ScopeType.BRANCH,
            ],
            allowedScopeKinds: ['SYSTEM', 'PLATFORM', 'ORGANIZATION', 'BRANCH'],
            ownershipPolicy: 'AUTHORITY_RESOLVED',
            accessType: 'ADMIN',
            delegationPolicy: 'NOT_ALLOWED',
            clientForcing: 'NONE',
            forcesClientScope: false,
            supportDelegationEligible: false,
        };
    }
    if (!MARKET_DATA_OPERATION_IDS.has(operationId))
        return {};
    const isReadOnly = MARKET_DATA_READ_OPERATION_IDS.has(operationId);
    return {
        module: 'MARKET_DATA',
        requiredModules: ['MARKET_DATA'],
        permission: isReadOnly
            ? 'organization:market-data:read'
            : 'organization:market-data:manage',
        permissionId: isReadOnly
            ? 'organization:market-data:read'
            : 'organization:market-data:manage',
        allowedPersonas: ['PLATFORM_ROOT', 'PLATFORM_ADMIN', 'ORG_ADMIN'],
        scopeTypes: [
            ScopeType.SYSTEM,
            ScopeType.PLATFORM,
            ScopeType.ORGANIZATION,
            ScopeType.BRANCH,
        ],
        allowedScopeKinds: ['SYSTEM', 'PLATFORM', 'ORGANIZATION', 'BRANCH'],
        ...(isReadOnly ? {} : { ownershipPolicy: 'AUTHORITY_RESOLVED' }),
        accessType: isReadOnly ? 'READ' : 'ADMIN',
        delegationPolicy: isReadOnly ? 'READ_ONLY' : 'NOT_ALLOWED',
        clientForcing: 'NONE',
        forcesClientScope: false,
        supportDelegationEligible: isReadOnly,
        ...(operationId === OID.MARKET_DATA_DELETE
            ? { implementationStatus: 'UNAVAILABLE' }
            : {}),
    };
}
function normalizePlatformScope(descriptor) {
    if (descriptor.ownershipPolicy !== 'PLATFORM')
        return {};
    const scopeTypes = new Set(descriptor.scopeTypes ?? []);
    scopeTypes.add(ScopeType.SYSTEM);
    scopeTypes.add(ScopeType.PLATFORM);
    const allowedScopeKinds = new Set(descriptor.allowedScopeKinds ?? []);
    allowedScopeKinds.add('SYSTEM');
    allowedScopeKinds.add('PLATFORM');
    return {
        scopeTypes: Array.from(scopeTypes),
        allowedScopeKinds: Array.from(allowedScopeKinds),
    };
}
function normalizePlatformModuleLifecycle(operationId) {
    return operationId === OID.PLATFORM_MODULES_ASSIGN ? { httpMethods: ['POST', 'PATCH'] } : {};
}
function enforceOperationSecurityInvariants(registry) {
    return Object.fromEntries(Object.entries(registry).map(([operationId, descriptor]) => {
        const portfolioPerformancePermissionId = PORTFOLIO_PERFORMANCE_PERMISSION_ID_BY_OPERATION[operationId];
        const canonicalPermissionId = portfolioPerformancePermissionId ??
            descriptor.permissionId ??
            CANONICAL_PERMISSION_ID_BY_LEGACY_PERMISSION[descriptor.permission];
        const normalizedDescriptor = {
            ...descriptor,
            ...normalizeReferenceDataAuthority(operationId, descriptor),
        };
        return [
            operationId,
            Object.freeze({
                ...normalizedDescriptor,
                ...normalizePlatformScope(normalizedDescriptor),
                ...normalizePlatformModuleLifecycle(operationId),
                ...normalizeMaintenanceDelegationOperation(normalizedDescriptor),
                ...(portfolioPerformancePermissionId
                    ? { permission: portfolioPerformancePermissionId }
                    : {}),
                ...(canonicalPermissionId
                    ? { permissionId: canonicalPermissionId }
                    : {}),
                ...(HIGH_SECURITY_AAL2_OPERATION_ID_SET.has(operationId)
                    ? { requiredAal: AuthenticationAssuranceLevel.AAL2 }
                    : {}),
                ...(DISABLED_ACCOUNT_SECURITY_OPERATION_ID_SET.has(operationId)
                    ? { implementationStatus: 'UNAVAILABLE' }
                    : {}),
            }),
        ];
    }));
}
export const CANONICAL_OPERATIONS_REGISTRY = Object.freeze(enforceOperationSecurityInvariants(MERGED_OPERATIONS_REGISTRY));
export class CanonicalOperationsService {
    registry;
    constructor(registry = CANONICAL_OPERATIONS_REGISTRY) {
        this.registry = registry;
    }
    resolve(operationId) {
        return this.registry[operationId];
    }
    resolveAll() {
        return this.registry;
    }
    getAllIds() {
        return Object.keys(this.registry);
    }
    exists(operationId) {
        return operationId in this.registry;
    }
    filter(predicate) {
        return Object.entries(this.registry)
            .filter(([, operation]) => predicate(operation))
            .reduce((accumulator, [id, operation]) => ({ ...accumulator, [id]: operation }), {});
    }
    getByModule(module) {
        return this.filter((operation) => operation.module === module);
    }
    getFinancialOperations() {
        return this.filter((operation) => operation.isFinancial);
    }
    getMakerCheckerOperations() {
        return this.filter((operation) => operation.makerCheckerRequired);
    }
    getAal2Operations() {
        return this.filter((operation) => operation.requiredAal === AuthenticationAssuranceLevel.AAL2);
    }
    validate(descriptor) {
        const errors = [];
        if (!descriptor.id)
            errors.push('Operation descriptor must have an id');
        if (!descriptor.name)
            errors.push(`Operation ${descriptor.id ?? '?'} must have a name`);
        if (!descriptor.permission)
            errors.push(`Operation ${descriptor.id ?? '?'} must have a permission`);
        if (!descriptor.module)
            errors.push(`Operation ${descriptor.id ?? '?'} must have a module`);
        if (!descriptor.requiredAal)
            errors.push(`Operation ${descriptor.id ?? '?'} must have requiredAal`);
        if (!descriptor.scopeTypes?.length)
            errors.push(`Operation ${descriptor.id ?? '?'} must have at least one scopeType`);
        if (descriptor.forcesClientScope === undefined)
            errors.push(`Operation ${descriptor.id ?? '?'} must define forcesClientScope`);
        if (descriptor.makerCheckerRequired === undefined)
            errors.push(`Operation ${descriptor.id ?? '?'} must define makerCheckerRequired`);
        if (!descriptor.idempotencyPolicy)
            errors.push(`Operation ${descriptor.id ?? '?'} must have idempotencyPolicy`);
        if (!descriptor.concurrencyPolicy)
            errors.push(`Operation ${descriptor.id ?? '?'} must have concurrencyPolicy`);
        if (!descriptor.auditPolicy)
            errors.push(`Operation ${descriptor.id ?? '?'} must have auditPolicy`);
        if (!descriptor.privacyClassification)
            errors.push(`Operation ${descriptor.id ?? '?'} must have privacyClassification`);
        if (!descriptor.allowedActorTypes?.length)
            errors.push(`Operation ${descriptor.id ?? '?'} must have at least one allowedActorType`);
        if (!descriptor.httpMethods?.length)
            errors.push(`Operation ${descriptor.id ?? '?'} must have at least one httpMethod`);
        if (descriptor.isReadOnly === undefined)
            errors.push(`Operation ${descriptor.id ?? '?'} must define isReadOnly`);
        if (descriptor.isFinancial === undefined)
            errors.push(`Operation ${descriptor.id ?? '?'} must define isFinancial`);
        if (descriptor.supportDelegationEligible === undefined)
            errors.push(`Operation ${descriptor.id ?? '?'} must define supportDelegationEligible`);
        if (descriptor.requiresEntitledModule === false &&
            !ENTITLEMENT_EXEMPT_MODULES.includes(descriptor.module)) {
            errors.push(`Operation ${descriptor.id ?? '?'} declares requiresEntitledModule: false but module ${descriptor.module} is not entitlement-exempt`);
        }
        return errors.length > 0 ? { valid: false, errors } : { valid: true };
    }
    validateAll() {
        const invalid = {};
        for (const [id, operation] of Object.entries(this.registry)) {
            const result = this.validate(operation);
            if (!result.valid && result.errors)
                invalid[id] = result.errors;
        }
        return invalid;
    }
}
export const canonicalOperationsService = new CanonicalOperationsService();
export * from './metadata.js';
export * from './operation-ids.js';
export * from './operation.js';
export * from './maintenance-delegation-policy.js';
export * from './platform.operations.js';
export * from './platform-integration.operations.js';
export * from './dashboard.operations.js';
export * from './market-data.operations.js';
export * from './portfolio-performance.operations.js';
export * from './portfolio-custody.operations.js';
export * from './suspense-cash.operations.js';
export * from './organization-governance.operations.js';
export * from './investment.operations.js';
export * from './access.operations.js';
export * from './tax-rule.operations.js';
export * from './fee-rule.operations.js';
export * from './search.operations.js';
export * from './market-data-correction.operations.js';
export * from './report-export.operations.js';
export { ACCOUNTING_OPERATIONS, RECONCILIATION_OPERATIONS, REGULATORY_OPERATIONS, IMPORT_OPERATIONS, } from './accounting.operations.js';
